import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { asArray, errorToast } from "../../shared/utils/helpers";
import { destroy2, patch, post2, put2 } from "../axios";
import { Garnish, Topping } from "../utils/interfaces";
import useBranchId from "./useBranchId";
import useCategoryQueryHook from "./useCategoryQueryHook";
import useCloudinaryUploadHook from "./useCloudinaryUploadHook";
import { getMenuStatusQueryOptions } from "../utils/queryOptions";
import useCountryConfigHook from "./useCountryConfigHook";
import { DeliveryMethods, VatEnum } from "../utils/enum";

const useCategoryHook = () => {
  const {
    getCloudinaryInfo,
    uploadImageToCloudinary,
    updateCloudinaryInfo,
    uploadImage,
  } = useCloudinaryUploadHook();
  const { t } = useTranslation();
  const branch = useBranchId();
  const { isBelgium } = useCountryConfigHook();
  const query = useCategoryQueryHook();
  const menuStatusQueryOptions = getMenuStatusQueryOptions(branch);
  const queryClient = useQueryClient();

  const menuUpdate = useMutation({
    onSuccess: async () => {
      toast.success(t("success.success"));
    },
    onError: async () => {
      errorToast();
    },
    mutationFn: (data: any) => {
      return patch(`internal/secure/pos/v1/menus/complete/${branch}`, {
        content: data.map((d) => ({
          ...d,
          name: asArray(d.name),
          description: asArray(d.description),
          item_type: undefined,
        })),
      });
    },
  });

  const categoryCreate = useMutation({
    mutationFn: (data: any) => {
      return post2(
        `internal/secure/pos/v1/menus/menu-item/${branch}/${data.parenItemId}/${data.position}`,
        {
          ...data,
          name: asArray(data.name),
          description: asArray(data.description),
          parenItemId: undefined,
          position: undefined,
          image: undefined,
        }
      );
    },
    onSuccess() {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast(t("errors.create"));
    },
  });

  const categoryRemove = useMutation({
    mutationFn: (menuItemId: number) => {
      return destroy2(
        `/internal/secure/pos/v1/menus/menu-item/${branch}/${menuItemId}`
      );
    },
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.delete"));
    },
    onError() {
      errorToast();
    },
  });

  const categoryReorder = useMutation({
    mutationFn: (data: any) =>
      put2(
        `internal/secure/pos/v1/menus/menu-item-position/${branch}/${0}/${
          data.oldIndex
        }/${data.newIndex}`,
        {}
      ),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  const categoryUpdate = useMutation({
    mutationFn: (cat: any) =>
      put2(`internal/secure/pos/v1/menus/menu-item/${branch}/${cat.item_id}`, {
        ...cat,
        name: asArray(cat.name),
        description: asArray(cat.description),
        item_id: undefined,
        child: undefined,
        topping_categories: undefined,
        sub_category: undefined,
        image: undefined,
        sub_content: undefined,
      }),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: async () => {
      errorToast();
      query.refetch();
    },
  });

  const productReorder = useMutation({
    mutationFn: (data: any) =>
      put2(
        `internal/secure/pos/v1/menus/menu-item-position/${branch}/${data.parentId}/${data.oldIndex}/${data.newIndex}`,
        {}
      ),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  const productCreate = useMutation({
    mutationFn: (product: any) =>
      post2(
        `internal/secure/pos/v1/menus/menu-item/${branch}/${product.parentId}/${product.position}`,
        {
          ...product,
          vat_enum_per_target: isBelgium
            ? {
                ...product.vat_enum_per_target,
                [DeliveryMethods.DINE_IN]: VatEnum.VAT_BE_HIGH,
              }
            : product.vat_enum_per_target,
          name: asArray(product.name),
          name_on_receipt: asArray(product.name_on_receipt),
          description: asArray(product.description),
          parentId: undefined,
          parent_code: undefined,
          position: undefined,
          item_type: "2",
        }
      ),

    onSuccess() {
      query.refetch();
      queryClient.invalidateQueries(menuStatusQueryOptions);
      toast.success(t("success.success"));
    },
    onError: () => {
      query.refetch();
      errorToast(t("errors.create"));
    },
  });

  const virtualCategoryProductAdd = useMutation({
    mutationFn: (data: any) =>
      post2(
        `internal/secure/pos/v1/menus/virtual/category/add/menu-item/${branch}/${data.itemId}/${data.parentId}/${data.position}`,
        { item_type: "2" }
      ),

    onSuccess() {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      query.refetch();
      errorToast(t("errors.create"));
    },
  });

  const virtualCategoryProductRemove = useMutation({
    mutationFn: (data: any) =>
      destroy2(
        `internal/secure/pos/v1/menus/virtual/category/delete/menu-item/${branch}/${data.itemId}/${data.parentId}`
      ),

    onSuccess() {
      query.refetch();
      toast.success(t("success.delete"));
    },
    onError: () => {
      query.refetch();
      errorToast();
    },
  });

  const productUpdate = useMutation({
    mutationFn: (product: any) =>
      put2(
        `internal/secure/pos/v1/menus/menu-item/${branch}/${product.item_id}`,
        {
          ...product,
          vat_enum_per_target: isBelgium
            ? {
                ...product.vat_enum_per_target,
                [DeliveryMethods.DINE_IN]: VatEnum.VAT_BE_HIGH,
              }
            : product.vat_enum_per_target,
          name: asArray(product.name),
          name_on_receipt: asArray(product.name_on_receipt),
          description: asArray(product.description),
          item_id: undefined,
          position: undefined,
          parent_code: undefined,
          sub_category: undefined,
          child: undefined,
          sub_content: undefined,
          vat_percentage: undefined,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(menuStatusQueryOptions);
      query.refetch();
      toast.success(t("success.success"));
    },
    onError() {
      errorToast();
    },
  });

  const productRemove = useMutation({
    mutationFn: (menuItemId: number) => {
      return destroy2(
        `/internal/secure/pos/v1/menus/menu-item/${branch}/${menuItemId}`
      );
    },
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.delete"));
    },
    onError: async () => {
      errorToast();
    },
  });

  const productPatch = useMutation({
    mutationFn: ({
      data,
      categoryId,
      type,
    }: {
      data: object;
      categoryId: number;
      type: "topping-category" | "allergens" | "labels" | "vats";
    }) => {
      return patch(
        `internal/secure/pos/v1/menus/${type}/${branch}/${categoryId}`,
        data
      );
    },
    onSuccess() {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast(t("errors.create"));
    },
  });

  const garnishUpdate = useMutation({
    mutationFn: (data: Garnish) =>
      put2(`internal/secure/pos/v1/menus/menu-item/${branch}/${data.item_id}`, {
        ...data,
        description: asArray(data.description),
        name: asArray(data.name),
        item_id: undefined,
        sub_content: undefined,
      }),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  const garnishRemove = useMutation({
    mutationFn: (menuItemId: number) => {
      return destroy2(
        `/internal/secure/pos/v1/menus/menu-item/${branch}/${menuItemId}`
      );
    },
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.delete"), { toastId: "garnishDelete" });
    },
    onError: () => {
      errorToast();
    },
  });

  const garnishCreate = useMutation({
    mutationFn: (submitData: any) => {
      return post2(`internal/secure/pos/v1/menus/menu-item/${branch}`, {
        ...submitData,
        description: asArray(submitData.description),
        name: asArray(submitData.name),
      });
    },
    onSuccess() {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast(t("errors.create"));
    },
  });

  const toppingUpdate = useMutation({
    mutationFn: (data: Topping) =>
      put2(`internal/secure/pos/v1/menus/menu-item/${branch}/${data.item_id}`, {
        ...data,
        description: asArray(data.description),
        name: asArray(data.name),
        item_id: undefined,
        sub_content: undefined,
        vat_percentage: undefined,
      }),
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast();
    },
  });

  const toppingRemove = useMutation({
    mutationFn: (menuItemId: number) => {
      return destroy2(
        `/internal/secure/pos/v1/menus/menu-item/${branch}/${menuItemId}`
      );
    },
    onSuccess: () => {
      query.refetch();
      toast.success(t("success.delete"), { toastId: "toppingDelete" });
    },
    onError: () => {
      errorToast();
    },
  });

  const toppingCreate = useMutation({
    mutationFn: (data: any) => {
      return post2(`internal/secure/pos/v1/menus/menu-item/${branch}`, {
        ...data,
        description: asArray(data.description),
        name: asArray(data.name),
        sub_content: undefined,
      });
    },
    onSuccess() {
      query.refetch();
      toast.success(t("success.success"));
    },
    onError: () => {
      errorToast(t("errors.create"));
    },
  });

  return {
    products: [],
    ...query,
    query,
    menuUpdate,
    categoryCreate,
    categoryRemove,
    categoryUpdate,
    categoryReorder,
    productCreate,
    productRemove,
    productReorder,
    productUpdate,
    productPatch,
    garnishUpdate,
    garnishRemove,
    garnishCreate,
    toppingUpdate,
    toppingRemove,
    toppingCreate,
    getCloudinaryInfo,
    uploadImageToCloudinary,
    updateCloudinaryInfo,
    uploadImage,
    virtualCategoryProductAdd,
    virtualCategoryProductRemove,
  };
};

export default useCategoryHook;
